import { validateCell, type AuthoringTable, type CellValue, type DraftChange } from '@/lib/authoring-api';
import type { WynikZapytania } from '@/lib/baza-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  EDYCJA W SIATCE WYNIKU → ZMIANY CONFIG-DOWN (etap 2 „jak SSMS", 28.09.2026).
 *
 *  ⚠️⚠️ SIATKA NIE PISZE DO BAZY. Składa te same zmiany co ręczny edytor (`I`/`P` po kluczu
 *  naturalnym) i oddaje je wspólnemu podglądowi — bramka, ostrzeżenia, uzasadnienie, a potem
 *  rozsyłka na CAŁĄ wyspę (najpierw master, potem maszyny). Tak ustalone z użytkownikiem:
 *  zapis nigdy do jednej bazy, żeby portal nie robił rozjazdów własną ręką.
 *
 *  ⚠️ KLUCZ I TYPY Z KATALOGU EDYTORA, nie z bazy: to katalog mówi, które kolumny potwierdziliśmy
 *  do zapisu i jak zamienić napis z siatki na wartość, którą przyjmie bramka.
 *  ⚠️ USUWANIE OD 29.09.2026 (etap 3) — wyłącznie tam, gdzie katalog daje akcję `D`, czyli
 *  w tabelach-liściach. Usunięcie wygrywa z edycją tego samego wiersza: łatka na wiersz, który
 *  zaraz zniknie, byłaby zmianą bez skutku, a bramka i tak by ją odrzuciła.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

export type NowyWiersz = { id: string; wartosci: Record<string, string> };

export type EdycjaWyniku = {
  /** `wiersz:kolumna` (indeksy w wyniku) → wpisany napis. */
  zmienione: Record<string, string>;
  nowe: NowyWiersz[];
  /** Indeksy wierszy wyniku oznaczonych do usunięcia. */
  usuniete: number[];
};

export const pustaEdycja = (): EdycjaWyniku => ({ zmienione: {}, nowe: [], usuniete: [] });

export type ZmianyZEdycji = { zmiany: DraftChange[]; bledy: string[] };

/** Indeks kolumny wyniku po nazwie — bez wielkości liter, jak SPS. */
export function indeksKolumny(wynik: WynikZapytania, nazwa: string): number {
  return wynik.kolumny.findIndex((k) => k.nazwa.toLowerCase() === nazwa.toLowerCase());
}

/** Kolumny klucza, których nie ma w wyniku — bez nich wiersza nie da się wskazać. */
export function brakujaceKolumnyKlucza(wynik: WynikZapytania, tabela: AuthoringTable): string[] {
  return tabela.key.filter((k) => indeksKolumny(wynik, k.name) < 0).map((k) => k.name);
}

/** Kolumna katalogu, którą wolno zmienić w tej komórce — albo `null`. */
export function kolumnaEdytowalna(wynik: WynikZapytania, tabela: AuthoringTable, c: number) {
  const nazwa = wynik.kolumny[c]?.nazwa.toLowerCase();
  const kol = tabela.columns.find((k) => k.name.toLowerCase() === nazwa);
  return kol !== undefined && kol.editable && kol.type !== null ? kol : null;
}

/** Klucz naturalny wiersza wyniku — z tych samych typów katalogu co przy łatce. */
function kluczWiersza(
  wynik: WynikZapytania,
  tabela: AuthoringTable,
  wiersz: readonly (string | null)[],
  r: number,
  bledy: string[],
): Record<string, string | number> {
  const pk: Record<string, string | number> = {};
  for (const k of tabela.key) {
    const v = validateCell(k.type, wiersz[indeksKolumny(wynik, k.name)] ?? '');
    if (v.error !== null || v.value === null || typeof v.value === 'boolean') {
      bledy.push(`Wiersz ${r + 1}: klucz ${k.name} — ${v.error ?? 'pusty'}`);
      continue;
    }
    pk[k.name] = v.value;
  }
  return pk;
}

/**
 * „USTAW WE WSZYSTKICH" — ta sama wartość w jednej kolumnie każdego wiersza wyniku (etap 3, 29.09.2026).
 *
 * ⚠️ WPISUJE DO EDYCJI, NIE DO ZMIAN: wiersze, które już mają tę wartość, odsieje `zmianyZEdycji`
 * (łatka „nic nie zmienia" i tak nie przeszłaby bramki), a wierszy oznaczonych do usunięcia nie łata.
 */
export function ustawWszedzie(wynik: WynikZapytania, e: EdycjaWyniku, c: number, raw: string): EdycjaWyniku {
  const zmienione = { ...e.zmienione };
  for (let r = 0; r < wynik.wiersze.length; r++) zmienione[`${r}:${c}`] = raw;
  return { ...e, zmienione };
}

/** „Usuń wszystkie" — każdy wiersz wyniku do usunięcia (tylko tam, gdzie katalog daje `D`). */
export const usunWszystkie = (wynik: WynikZapytania, e: EdycjaWyniku): EdycjaWyniku => ({
  ...e,
  usuniete: wynik.wiersze.map((_, r) => r),
});

export const wszystkieUsuniete = (wynik: WynikZapytania, e: EdycjaWyniku): boolean =>
  wynik.wiersze.length > 0 && e.usuniete.length === wynik.wiersze.length;

/**
 * Na ile operacji pójdzie hurt — TA SAMA REGUŁA CO `podzielHurt` w `api/src/authoring/hurt.ts`:
 * osobno każda akcja, po `rozmiar` zmian. Tylko do napisu przed podglądem; podział liczy serwer.
 */
export function liczbaOperacji(zmiany: readonly Pick<DraftChange, 'action'>[], rozmiar: number): number {
  const wgAkcji = new Map<string, number>();
  for (const z of zmiany) wgAkcji.set(z.action, (wgAkcji.get(z.action) ?? 0) + 1);
  return [...wgAkcji.values()].reduce((n, ile) => n + Math.ceil(ile / rozmiar), 0);
}

export function zmianyZEdycji(wynik: WynikZapytania, tabela: AuthoringTable, e: EdycjaWyniku): ZmianyZEdycji {
  const zmiany: DraftChange[] = [];
  const bledy: string[] = [];

  /** Usunięcia najpierw: nowy wiersz o kluczu właśnie usuniętego ma trafić w wolne miejsce. */
  const usuniete = new Set(e.usuniete);
  if (usuniete.size > 0 && !tabela.actions.includes('D')) {
    bledy.push(`Tabela ${tabela.name} nie pozwala usuwać wierszy — to rekordy nadrzędne`);
  } else {
    for (const r of [...usuniete].sort((a, b) => a - b)) {
      const wiersz = wynik.wiersze[r];
      if (wiersz === undefined) continue;
      zmiany.push({ tableName: tabela.name, action: 'D', pk: kluczWiersza(wynik, tabela, wiersz, r, bledy) });
    }
  }

  const wgWiersza = new Map<number, [number, string][]>();
  for (const [adres, raw] of Object.entries(e.zmienione)) {
    const [r, c] = adres.split(':').map(Number) as [number, number];
    const lista = wgWiersza.get(r) ?? [];
    lista.push([c, raw]);
    wgWiersza.set(r, lista);
  }

  for (const [r, komorki] of [...wgWiersza].sort((a, b) => a[0] - b[0])) {
    const wiersz = wynik.wiersze[r];
    if (wiersz === undefined || usuniete.has(r)) continue;
    const payload: Record<string, CellValue> = {};
    for (const [c, raw] of komorki) {
      const kol = kolumnaEdytowalna(wynik, tabela, c);
      if (kol === null || raw === (wiersz[c] ?? '')) continue;
      const v = validateCell(kol.type, raw);
      if (v.error !== null) bledy.push(`Wiersz ${r + 1}, ${kol.name}: ${v.error}`);
      payload[kol.name] = v.value;
    }
    if (Object.keys(payload).length === 0) continue;
    zmiany.push({ tableName: tabela.name, action: 'P', pk: kluczWiersza(wynik, tabela, wiersz, r, bledy), payload });
  }

  for (const [i, n] of e.nowe.entries()) {
    const pk: Record<string, string | number> = {};
    const payload: Record<string, CellValue> = {};
    for (const k of tabela.key) {
      const v = validateCell(k.type, n.wartosci[k.name] ?? '');
      if (v.error !== null || v.value === null || typeof v.value === 'boolean') {
        bledy.push(`Nowy wiersz ${i + 1}: klucz ${k.name} — ${v.error ?? 'pusty'}`);
        continue;
      }
      pk[k.name] = v.value;
      payload[k.name] = v.value;
    }
    for (const kol of tabela.columns) {
      const raw = n.wartosci[kol.name];
      if (raw === undefined || raw.trim() === '' || !kol.editable || kol.type === null) continue;
      const v = validateCell(kol.type, raw);
      if (v.error !== null) bledy.push(`Nowy wiersz ${i + 1}, ${kol.name}: ${v.error}`);
      payload[kol.name] = v.value;
    }
    zmiany.push({ tableName: tabela.name, action: 'I', pk, payload });
  }

  return { zmiany, bledy };
}
